import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  initializeKeycloak,
  keycloak,
  readKeycloakClaims,
  refreshKeycloakSession,
  syncKeycloakAuthStore,
} from "@/lib/auth/keycloak";
import { clearAuthState } from "@/lib/auth-store";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AppSession {
  accessToken: string;
  idToken?: string;
  expiresAt?: number;
  preferredUsername?: string;
  user: {
    name?: string;
    email?: string;
  };
}

interface AuthContextValue {
  status: AuthStatus;
  session: AppSession | null;
  login: (callbackUrl?: string, forceFreshLogin?: boolean) => Promise<void>;
  logout: (callbackUrl?: string) => Promise<void>;
  refresh: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const readSession = (): AppSession | null => {
  if (!keycloak.authenticated || !keycloak.token) return null;
  const claims = readKeycloakClaims();
  return {
    accessToken: keycloak.token,
    idToken: keycloak.idToken,
    expiresAt: claims.exp,
    preferredUsername: claims.preferred_username,
    user: {
      name: claims.name ?? claims.preferred_username,
      email: claims.email,
    },
  };
};

export function AppAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<AppSession | null>(null);

  const commitSession = useCallback(() => {
    const nextSession = readSession();
    setSession(nextSession);
    setStatus(nextSession ? "authenticated" : "unauthenticated");
    if (nextSession) syncKeycloakAuthStore();
    else clearAuthState();
  }, []);

  const clearSession = useCallback(() => {
    clearAuthState();
    setSession(null);
    setStatus("unauthenticated");
  }, []);

  const refreshSession = useCallback(async () => {
    const refreshed = await refreshKeycloakSession();
    if (refreshed) commitSession();
    else clearSession();
    return refreshed;
  }, [clearSession, commitSession]);

  useEffect(() => {
    let active = true;
    const refreshWhileActive = () => {
      void refreshKeycloakSession().then((refreshed) => {
        if (!active) return;
        if (refreshed) commitSession();
        else clearSession();
      });
    };
    keycloak.onAuthSuccess = commitSession;
    keycloak.onAuthRefreshSuccess = commitSession;
    keycloak.onAuthLogout = commitSession;
    keycloak.onTokenExpired = () => {
      refreshWhileActive();
    };

    void initializeKeycloak()
      .then(() => {
        if (!active) return;
        const canonicalOrigin = new URL(
          import.meta.env.VITE_APP_ORIGIN?.trim()
            || (import.meta.env.DEV ? window.location.origin : "https://bpm.montaner.com"),
        ).origin;
        if (window.location.origin !== canonicalOrigin) {
          const destination = new URL(canonicalOrigin);
          destination.pathname = window.location.pathname;
          destination.search = window.location.search;
          destination.hash = window.location.hash;
          window.location.replace(destination.toString());
          return;
        }
        commitSession();
      })
      .catch(() => {
        if (!active) return;
        clearSession();
      });

    const refreshInterval = window.setInterval(() => {
      if (!keycloak.authenticated) return;
      refreshWhileActive();
    }, 30_000);

    return () => {
      active = false;
      window.clearInterval(refreshInterval);
    };
  }, [clearSession, commitSession]);

  const login = useCallback(async (callbackUrl = "/task-list", forceFreshLogin = false) => {
    const redirectUri = new URL(callbackUrl, window.location.origin).toString();
    await keycloak.login({
      redirectUri,
      prompt: forceFreshLogin ? "login" : undefined,
    });
  }, []);

  const logout = useCallback(async (callbackUrl = "/task-list") => {
    clearAuthState();
    setSession(null);
    setStatus("unauthenticated");
    const returnUrl = new URL("/", window.location.origin);
    returnUrl.searchParams.set("loggedOut", "1");
    returnUrl.searchParams.set("callbackUrl", callbackUrl);
    await keycloak.logout({ redirectUri: returnUrl.toString() });
  }, []);

  const refresh = useCallback(async () => {
    return refreshSession();
  }, [refreshSession]);

  const value = useMemo<AuthContextValue>(() => ({ status, session, login, logout, refresh }), [status, session, login, logout, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthSession(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuthSession debe usarse dentro de AppAuthProvider");
  return value;
}
